import { renderToString } from 'react-dom/server';
import { MultiLineString, Point } from 'geojson';
import {
  LngLatBounds,
  Map,
  Popup,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import {
  HeightRestriction,
  RoadWorkIcon,
} from '@/components/ui/icons/map/almanac';
import {
  EventObservationFrame,
  getFramesForMapFeature,
  getFramesForRoadwork,
  MapFeatureFrame,
} from '@/api/developer';
import { hereReverseLookup } from '@/api/map';
import { loadSvgImage } from '../svg';

export const ALMANAC_SOURCE_ID = 'almanac-source';
export const HEIGHT_RESTRICTION_LAYER_ID_ACTIVE =
  'almanac-height-restriction-layer-active';
export const HEIGHT_RESTRICTION_SOURCE_LAYER_ID = 'almanac-map-feature';

export const ROAD_WORK_LAYER_ID = 'almanac-road-work-layer';
export const ROAD_WORK_ICON_LAYER_ID = 'almanac-road-work-icon-layer';
export const ROAD_WORK_SOURCE_LAYER_ID = 'almanac-map-events';

export const addAlmanacSource = (map: Map) => {
  map.addSource(ALMANAC_SOURCE_ID, {
    type: 'vector',
    url:
      'pmtiles://' +
      'https://network-tiles.s3.us-west-2.amazonaws.com/almanac-features_664ba98a17b5714daddf1b75.pmtiles',
    promoteId: 'id',
  });
};

export const addHeightRestrictionLayer = async (map: Map) => {
  map.addImage(
    HeightRestriction.name,
    await loadSvgImage(HeightRestriction.svg),
  );

  const layoutSharedProps: SymbolLayerSpecification['layout'] = {
    'text-font': ['Lato Bold', 'Arial Unicode MS Regular'],
    // prettier-ignore
    'icon-size': ['interpolate', ['linear'], ['zoom'], 12, 0.2, 15, 0.35],
    'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 15, 14],
    'icon-padding': 0,
    'icon-allow-overlap': false,
    'text-field': ['get', 'height'],
    'text-anchor': 'bottom',
    'text-optional': false,
    'text-offset': [0.2, -0.9],
    'text-allow-overlap': true,
    'text-ignore-placement': true,
  };
  const sharedPaintProps: SymbolLayerSpecification['paint'] = {
    'text-color': '#ffffff',
    'text-halo-width': 2,
    'text-halo-color': '#000000',
    // prettier-ignore
    'text-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
  };
  // // Add the highlighted layer that only renders on top
  map.addLayer({
    id: HEIGHT_RESTRICTION_LAYER_ID_ACTIVE,
    type: 'symbol',
    source: ALMANAC_SOURCE_ID,
    // Filter out ones  height attribute and not empty string
    filter: ['all', ['has', 'height'], ['!=', 'height', '']],
    'source-layer': HEIGHT_RESTRICTION_SOURCE_LAYER_ID,
    paint: sharedPaintProps,
    layout: {
      'icon-image': HeightRestriction.name,
      ...layoutSharedProps,
    },
  });
};

export const addHeightRestrictionHoverHandler = (map: Map) => {
  let hoveredFeatureId: string | null = null;
  // Show height restriction value on hover
  map.on('mouseenter', HEIGHT_RESTRICTION_LAYER_ID_ACTIVE, e => {
    const features = map.queryRenderedFeatures(e.point);
    if (features.length === 0) return;

    // set the hover feature state
    map.setFeatureState(
      {
        source: ALMANAC_SOURCE_ID,
        sourceLayer: HEIGHT_RESTRICTION_SOURCE_LAYER_ID,
        id: features[0]!.properties.id,
      },
      { hover: true },
    );
    hoveredFeatureId = features[0]!.properties.id;
  });
  map.on('mouseleave', HEIGHT_RESTRICTION_LAYER_ID_ACTIVE, () => {
    if (hoveredFeatureId) {
      map.setFeatureState(
        {
          source: ALMANAC_SOURCE_ID,
          sourceLayer: HEIGHT_RESTRICTION_SOURCE_LAYER_ID,
          id: hoveredFeatureId,
        },
        { hover: false },
      );
      hoveredFeatureId = null;
    }
  });

  // Add pointer cursor on hover
  map.on('mouseenter', HEIGHT_RESTRICTION_LAYER_ID_ACTIVE, e => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', HEIGHT_RESTRICTION_LAYER_ID_ACTIVE, e => {
    map.getCanvas().style.cursor = '';
  });
};

export const HeightRestrictionPopup = ({
  height,
  address,
  frame,
}: {
  height: string;
  address: string;
  frame?: MapFeatureFrame;
}) => {
  return (
    <div className="z-10 max-h-[600px] w-[512px] max-w-lg overflow-auto rounded-lg border border-neutral-700 bg-neutral-900 p-4 text-neutral-0">
      <div className="mb-2 text-xl font-bold">
        {height.replace(/"/g, ' in').replace(/'/g, ' ft')}
      </div>
      {address && <div className="mb-2 text-sm font-bold">{address}</div>}
      {frame && (
        <div className="relative flex aspect-[2/1] w-full items-center justify-center overflow-hidden rounded-lg bg-neutral-700">
          <div className="absolute left-2 top-2 rounded bg-neutral-900/80 px-1.5 py-1 font-lato text-xs font-bold">
            {new Date(frame.t).toLocaleString()}
          </div>
          <img
            src={frame.url}
            alt="Map feature frame"
            className="size-full p-0 "
          />
        </div>
      )}
    </div>
  );
};

export const showPopupOnHeightRestrictionClick = (map: Map) => {
  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    maxWidth: 'none',
    offset: 10,
  });
  map.on('click', e => {
    if (popup.isOpen()) popup.remove();
  });
  map.on('click', HEIGHT_RESTRICTION_LAYER_ID_ACTIVE, async e => {
    // open a popup with more information
    const features = map.queryRenderedFeatures(e.point);
    if (!features.length) return;

    const targetFeature = features[0]!;
    const height = targetFeature.properties.height as string;
    // Convert units from x"y' to x ft y
    height.replace(/"/g, 'ft').replace(/'/g, 'in');

    map.getCanvas().style.cursor = 'wait';
    const framesRes = await getFramesForMapFeature(targetFeature.properties.id);
    map.getCanvas().style.cursor = '';
    let frame: MapFeatureFrame | undefined;
    if ('frames' in framesRes && framesRes.frames.length > 0) {
      frame = framesRes.frames[0];
    }
    const { address } = await hereReverseLookup(
      (targetFeature.geometry as Point).coordinates,
    );

    popup
      .setLngLat(e.lngLat)
      .setHTML(
        renderToString(
          <HeightRestrictionPopup
            height={height}
            address={address!}
            frame={frame}
          />,
        ),
      )
      .addTo(map);
  });
};

export const addRoadWorkLayers = async (map: Map) => {
  // Add line layer
  map.addLayer({
    id: ROAD_WORK_LAYER_ID,
    type: 'line',
    source: ALMANAC_SOURCE_ID,
    'source-layer': ROAD_WORK_SOURCE_LAYER_ID,
    paint: {
      'line-color': '#FF9500',
      // line width changes according to zoom level
      'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 18, 22],
    },
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
  });

  // Add icon layer
  map.addImage(RoadWorkIcon.name, await loadSvgImage(RoadWorkIcon.svg));
  map.addLayer({
    id: ROAD_WORK_ICON_LAYER_ID,
    type: 'symbol',
    source: ALMANAC_SOURCE_ID,
    'source-layer': ROAD_WORK_SOURCE_LAYER_ID,
    layout: {
      'icon-image': RoadWorkIcon.name,
      'icon-rotation-alignment': 'viewport',
      'icon-padding': 70,
      'symbol-placement': 'line-center',
      'icon-size': ['interpolate', ['linear'], ['zoom'], 12, 0.2, 15, 0.5],
      'icon-allow-overlap': false,
    },
  });
};

export const RoadWorkPopup = ({
  address,
  frame,
}: {
  address?: string;
  frame?: EventObservationFrame;
}) => {
  return (
    <div
      id="almanac-roadwork-popup"
      className="z-10 max-h-[600px] w-[512px] max-w-lg overflow-auto rounded-lg border border-neutral-700 bg-neutral-900 p-4 text-neutral-0">
      <div className="mb-2 text-xl font-bold">Roadwork</div>
      {address && <div className="mb-2 text-sm font-bold">{address}</div>}
      {frame && (
        <div className="relative flex aspect-[2/1] w-full items-center justify-center overflow-hidden rounded-lg bg-neutral-700">
          <div className="absolute left-2 top-2 rounded bg-neutral-900/80 px-1.5 py-1 font-lato text-xs font-bold">
            {new Date(frame.timestamp).toLocaleString()}
          </div>
          <img
            src={frame.url}
            alt="Map feature frame"
            className="size-full p-0 "
          />
        </div>
      )}
    </div>
  );
};

export const showPopupOnRoadWorkClick = (map: Map) => {
  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    maxWidth: 'none',
    offset: 10,
  });
  // Add pointer on hover
  map.on(
    'mouseenter',
    ROAD_WORK_LAYER_ID,
    () => (map.getCanvas().style.cursor = 'pointer'),
  );
  map.on(
    'mouseleave',
    ROAD_WORK_LAYER_ID,
    () => (map.getCanvas().style.cursor = ''),
  );

  map.on('click', e => {
    if (popup.isOpen()) popup.remove();
  });
  map.on('click', ROAD_WORK_LAYER_ID, async e => {
    const features = map.queryRenderedFeatures(e.point);
    if (!features.length) return;

    const targetFeature = features[0]!;
    // TODO: re-enable after pipeline fix
    // let attrs: Array<any> = [];
    // try {
    //   attrs = JSON.parse(targetFeature.properties.attributes);
    // } catch (error) {
    //   console.error('Failed to parse feature attributes', error);
    // }

    map.getCanvas().style.cursor = 'wait';
    let frame: EventObservationFrame | undefined;
    try {
      const res = await getFramesForRoadwork(targetFeature.properties.id);
      if ('frames' in res && res.frames.length > 0) {
        frame = res.frames[0];
      }
    } catch (error) {
      console.error('Failed to get frames for roadwork', error);
    }

    // Find address
    const coords =
      targetFeature.geometry.type === 'LineString'
        ? targetFeature.geometry.coordinates[0]
        : (targetFeature.geometry as MultiLineString).coordinates[0][0];
    const { address } = await hereReverseLookup(coords);
    map.getCanvas().style.cursor = '';

    try {
      const bounds = new LngLatBounds(e.lngLat, e.lngLat);
      map.fitBounds(bounds, { padding: 200, maxZoom: 15.5 });
    } catch (error) {
      console.error('Failed to fit bounds', error);
    }

    popup
      .setLngLat(e.lngLat)
      .setHTML(
        renderToString(<RoadWorkPopup address={address} frame={frame} />),
      )
      .addTo(map);
  });
};
